import { getAccessToken, refreshAccessToken } from "./authService";

// Get API base URL from environment variable
// In production, REACT_APP_API_BASE_URL should be set to HTTPS URL for security
// Falls back to localhost in development
const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_BASE_URL;
  const defaultApiUrl = "http://localhost:3000/api";

  // In production, warn if environment variable is not set
  if (process.env.NODE_ENV === "production") {
    if (!envUrl) {
      console.error(
        "CRITICAL: REACT_APP_API_BASE_URL is not set in production. " +
          "Using default URL. Please set REACT_APP_API_BASE_URL in .env.production",
      );
      return defaultApiUrl;
    }
    // Warn if using HTTP in production (but respect the explicit configuration)
    if (envUrl.startsWith("http://")) {
      console.warn(
        "WARNING: API_BASE_URL is using HTTP in production. " +
          "This may cause mixed-content warnings if frontend is served over HTTPS. " +
          "Consider setting up SSL/HTTPS for the backend server.",
      );
    }
    return envUrl;
  }

  // In development, use environment variable or fallback to localhost
  const apiUrl = envUrl || defaultApiUrl;
  console.log("🔗 API Base URL:", apiUrl);
  return apiUrl;
};

const API_BASE_URL = getApiBaseUrl();

// Global flag to prevent ALL requests after auth failure
let isAuthenticationFailed = false;
let isRedirectingToLogin = false;

// Function to check and set auth failure state
const markAuthenticationFailed = () => {
  if (!isAuthenticationFailed) {
    console.error("🚫 AUTHENTICATION FAILED - BLOCKING ALL REQUESTS");
    isAuthenticationFailed = true;
    isRedirectingToLogin = true;

    // Store in sessionStorage to persist across hook re-renders
    sessionStorage.setItem("auth_failed", "true");

    // Redirect immediately
    setTimeout(() => {
      console.log("🔄 Redirecting to login...");
      window.location.href = "/login";
    }, 50);
  }
};

// Check if auth has already failed
const checkAuthenticationFailed = () => {
  if (
    isAuthenticationFailed ||
    sessionStorage.getItem("auth_failed") === "true"
  ) {
    return true;
  }
  return false;
};

class ApiService {
  async request(endpoint, options = {}, retryCount = 0) {
    // IMMEDIATELY block if auth has failed
    if (checkAuthenticationFailed()) {
      console.warn("⛔ Request blocked - authentication failed:", endpoint);
      const error = new Error("Authentication failed - redirecting to login");
      error.status = 401;
      error.isAuthError = true;
      throw error;
    }

    const url = `${API_BASE_URL}${endpoint}`;

    // Get access token and add to headers
    const token = getAccessToken();
    const config = {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      // Handle 401 Unauthorized - try to refresh token
      if (response.status === 401 && retryCount === 0) {
        console.log("401 Unauthorized - attempting token refresh");
        const refreshResult = await refreshAccessToken();

        if (refreshResult.success) {
          // Retry request with new token
          console.log("Token refreshed successfully - retrying request");
          return this.request(endpoint, options, retryCount + 1);
        } else {
          // Refresh failed - mark auth failed and redirect
          console.error("❌ Token refresh failed");
          markAuthenticationFailed();

          const error = new Error("Session expired. Please log in again.");
          error.status = 401;
          error.isAuthError = true;
          throw error;
        }
      }

      // Handle 401 after retry attempt
      if (response.status === 401 && retryCount > 0) {
        console.error("❌ Authentication still failed after retry");
        markAuthenticationFailed();

        const error = new Error("Session expired. Please log in again.");
        error.status = 401;
        error.isAuthError = true;
        throw error;
      }

      if (!response.ok) {
        // For 400 and 500 errors, show generic message to users
        let errorMessage =
          "Unable to process your request right now. Please try again later.";
        let errorDetails = null;

        // Parse error details for logging/debugging and error handling
        try {
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            const errorData = await response.json();
            errorDetails = errorData;

            // For mapping validation errors, use the detailed message from backend
            if (
              errorData.error === "Mapping validation failed" ||
              errorData.error === "No mapping found" ||
              errorData.error === "No valid records to import"
            ) {
              errorMessage =
                errorData.message || errorData.error || errorMessage;
            } else if (errorData.message) {
              errorMessage = errorData.message;
            } else if (errorData.error) {
              errorMessage = errorData.error;
            }
          } else {
            // Try to get text response
            const errorText = await response.text();
            if (errorText) {
              try {
                const errorData = JSON.parse(errorText);
                errorDetails = errorData;
                if (errorData.message) {
                  errorMessage = errorData.message;
                } else if (errorData.error) {
                  errorMessage = errorData.error;
                }
              } catch {
                errorDetails = { raw: errorText };
              }
            }
          }
        } catch (parseError) {
          // If we can't parse the error response, log for debugging
          console.warn("Could not parse error response:", parseError);
        }

        // For 409 Conflict, keep the detailed error message from backend
        // For other non-400/500 errors, use a generic message
        if (
          response.status !== 400 &&
          response.status !== 500 &&
          response.status !== 409
        ) {
          errorMessage = `HTTP error! status: ${response.status}`;
        }

        const error = new Error(errorMessage);
        error.status = response.status;
        error.statusText = response.statusText;
        error.details = errorDetails;
        error.endpoint = endpoint;
        throw error;
      }

      return await response.json();
    } catch (error) {
      // Handle "Failed to fetch" errors (network errors, CORS, etc.)
      if (
        error.message === "Failed to fetch" ||
        error.message.includes("Failed to fetch") ||
        error.name === "TypeError" ||
        (error.message && error.message.toLowerCase().includes("network"))
      ) {
        const networkError = new Error("Unable to load the data");
        networkError.status = 0; // Network error status
        networkError.isNetworkError = true;
        networkError.originalError = error;
        throw networkError;
      }

      // Only log if it's not an error we created (to avoid duplicate logs)
      if (!error.status) {
        console.error("API request failed:", error);
      } else {
        console.error("API request failed:", {
          endpoint: error.endpoint,
          status: error.status,
          statusText: error.statusText,
          message: error.message,
          details: error.details,
        });
      }
      throw error;
    }
  }

  // Import inventory data
  async importInventory(formData, retryCount = 0) {
    // IMMEDIATELY block if auth has failed
    if (checkAuthenticationFailed()) {
      console.warn("⛔ Import request blocked - authentication failed");
      const error = new Error("Authentication failed - redirecting to login");
      error.status = 401;
      error.isAuthError = true;
      throw error;
    }

    const url = `${API_BASE_URL}/inventory-import/import`;

    // Get access token and add to headers
    const token = getAccessToken();
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        method: "POST",
        headers, // Authorization header for JWT
        body: formData, // FormData object - don't set Content-Type header, let browser set it with boundary
        // Note: Don't set Content-Type header for FormData, browser will set it automatically with boundary
      });

      // Handle 401 Unauthorized - try to refresh token
      if (response.status === 401 && retryCount === 0) {
        console.log("401 Unauthorized - attempting token refresh");
        const refreshResult = await refreshAccessToken();

        if (refreshResult.success) {
          // Retry request with new token
          console.log("Token refreshed successfully - retrying import");
          return this.importInventory(formData, retryCount + 1);
        } else {
          // Refresh failed - mark auth failed
          console.error("❌ Token refresh failed");
          markAuthenticationFailed();

          const error = new Error("Session expired. Please log in again.");
          error.status = 401;
          error.isAuthError = true;
          throw error;
        }
      }

      // Handle 401 after retry attempt
      if (response.status === 401 && retryCount > 0) {
        console.error("❌ Authentication still failed after retry");
        markAuthenticationFailed();

        const error = new Error("Session expired. Please log in again.");
        error.status = 401;
        error.isAuthError = true;
        throw error;
      }

      if (!response.ok) {
        // Try to parse error response to get actual error message
        let errorMessage =
          "Unable to process your request right now. Please try again later.";
        let errorDetails = null;

        try {
          const errorData = await response.json();
          // Use the message from the backend if available
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            // Fallback to error field if message not available
            errorMessage = errorData.error;
          }
          // Include details if available - preserve full structure
          if (errorData.details) {
            errorDetails = errorData.details;
          }
          if (errorData.data) {
            // Merge data into details if details exists, otherwise use data
            errorDetails = errorDetails
              ? { ...errorDetails, ...errorData.data }
              : errorData.data;
          }
          // Preserve error field for categorization
          if (errorData.error && !errorDetails) {
            errorDetails = { error: errorData.error };
          } else if (errorData.error && errorDetails) {
            errorDetails.error = errorData.error;
          }
          // Log full error for debugging
          console.error("Import API error response:", response.status);
          console.error("Import API error details:", errorData);
        } catch (parseError) {
          // If JSON parsing fails, try to get text
          try {
            const errorText = await response.text();
            if (errorText) {
              errorMessage = errorText;
            }
          } catch (textError) {
            // Keep default message if all parsing fails
            console.error("Import API error response:", response.status);
            console.error("Failed to parse error response:", parseError);
          }
        }

        const error = new Error(errorMessage);
        error.status = response.status;
        if (errorDetails) {
          error.details = errorDetails;
        }
        throw error;
      }

      return await response.json();
    } catch (error) {
      // Handle "Failed to fetch" errors (network errors, CORS, etc.)
      if (
        error.message === "Failed to fetch" ||
        error.message.includes("Failed to fetch") ||
        error.name === "TypeError" ||
        (error.message && error.message.toLowerCase().includes("network"))
      ) {
        const networkError = new Error("Unable to load the data");
        networkError.status = 0; // Network error status
        networkError.isNetworkError = true;
        networkError.originalError = error;
        throw networkError;
      }

      console.error("Import API request failed:", error);
      throw error;
    }
  }

  // Get tenants list
  async getTenants() {
    return this.request("/tenants");
  }

  // Get inventory data
  async getInventory(tenantId) {
    return this.request(`/inventory?tenant_id=${tenantId}`);
  }

  // Update inventory item
  async updateInventoryItem(itemId, data) {
    return this.request(`/inventory/${itemId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  // Create inventory item
  async createInventoryItem(data) {
    return this.request("/inventory", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // Get import data with dynamic columns
  // Uses: GET /api/inventory-import/data?provider=Nitel&category=Data&tenant_id=1&page=1&pageSize=2&import_id=4
  async getImportData(provider, category, tenantId, pagination = {}) {
    const params = new URLSearchParams();
    if (provider) params.append("provider", provider);
    if (category) params.append("category", category);
    if (tenantId) params.append("tenant_id", tenantId);
    if (pagination.importId) params.append("import_id", pagination.importId);
    if (pagination.page) params.append("page", pagination.page);
    if (pagination.pageSize) params.append("pageSize", pagination.pageSize);

    const queryString = params.toString();
    const url = queryString
      ? `/inventory-import/data?${queryString}`
      : "/inventory-import/data";

    return this.request(url);
  }

  // Get import records by import ID
  async getImportRecords(importId, page = 1, pageSize = 20) {
    return this.request(
      `/inventory-import/jobs/${importId}/records?page=${page}&pageSize=${pageSize}`,
    );
  }

  // Get import records data with dynamic mapping
  // Uses: GET /api/inventory-import/jobs/:importId/data?provider=Nitel&category=Data&tenant_id=1&page=1&pageSize=20&import_status=new
  async getImportRecordsData(
    importId,
    provider,
    category,
    tenantId,
    page = 1,
    pageSize = 20,
    importStatus = null,
  ) {
    const params = new URLSearchParams();
    if (provider) params.append("provider", provider);
    if (category) params.append("category", category);
    if (tenantId) params.append("tenant_id", tenantId);
    params.append("page", page);
    params.append("pageSize", pageSize);

    // Add status filter only if provided and not 'all'
    if (importStatus && importStatus !== "all" && importStatus !== null) {
      // Validate status value before sending
      const validStatuses = ["new", "updated", "same", "error"];
      if (validStatuses.includes(importStatus)) {
        params.append("import_status", importStatus);
      }
    }

    const queryString = params.toString();
    return this.request(
      `/inventory-import/jobs/${importId}/data?${queryString}`,
    );
  }

  async getGroupedImportRecordsData(
    importId,
    provider,
    category,
    tenantId,
    page = 1,
    pageSize = 20,
    importStatus = null,
    sortBy = null,
    sortOrder = "asc",
  ) {
    // Validate required parameters
    if (!importId) {
      throw new Error("Import ID is required");
    }
    if (
      !provider ||
      typeof provider !== "string" ||
      provider.trim().length === 0
    ) {
      throw new Error("Provider is required and must be a non-empty string");
    }
    if (
      !category ||
      typeof category !== "string" ||
      category.trim().length === 0
    ) {
      throw new Error("Category is required and must be a non-empty string");
    }
    if (!tenantId) {
      throw new Error("Tenant ID is required");
    }

    const params = new URLSearchParams();
    params.append("provider", provider.trim());
    params.append("category", category.trim());
    params.append("tenant_id", tenantId.toString().trim());
    params.append("page", page);
    params.append("pageSize", pageSize);

    // Add status filter only if provided and not 'all'
    if (importStatus && importStatus !== "all" && importStatus !== null) {
      // Validate status value before sending
      const validStatuses = ["new", "updated", "same", "error"];
      if (validStatuses.includes(importStatus)) {
        params.append("import_status", importStatus);
        console.log(
          `[API Service] Adding import_status filter: ${importStatus}`,
        );
      } else {
        console.warn(
          `[API Service] Invalid import_status value: ${importStatus}, skipping filter`,
        );
      }
    } else {
      console.log(
        `[API Service] No import_status filter applied (value: ${importStatus})`,
      );
    }

    // Add sorting parameters if provided
    if (sortBy) {
      params.append("sortBy", sortBy);
      params.append("sortOrder", sortOrder);
    }

    const queryString = params.toString();
    try {
      return await this.request(
        `/inventory-import/jobs/${importId}/grouped?${queryString}`,
      );
    } catch (error) {
      // Handle specific function errors
      if (
        error.message &&
        (error.message.includes("fn is not a function") ||
          error.message.includes("is not a function"))
      ) {
        const functionError = new Error(
          "Server configuration error. Please contact support or try refreshing the page.",
        );
        functionError.status = error.status || 500;
        functionError.isConfigurationError = true;
        functionError.originalError = error;
        throw functionError;
      }
      throw error;
    }
  }

  // Get import job metadata including field mapping and columns
  // Uses: GET /api/inventory-import/jobs/:importId/metadata?provider=Nitel&category=Data&tenant_id=1
  async getImportJobMetadata(importId, provider, category, tenantId) {
    // Validate required parameters
    if (!importId) {
      throw new Error("Import ID is required");
    }
    if (
      !provider ||
      typeof provider !== "string" ||
      provider.trim().length === 0
    ) {
      throw new Error("Provider is required and must be a non-empty string");
    }
    if (
      !category ||
      typeof category !== "string" ||
      category.trim().length === 0
    ) {
      throw new Error("Category is required and must be a non-empty string");
    }
    if (!tenantId) {
      throw new Error("Tenant ID is required");
    }

    // Validate importId is a valid number
    const importIdNum = Number(importId);
    if (!Number.isInteger(importIdNum) || importIdNum < 1) {
      throw new Error("Import ID must be a positive integer");
    }

    const params = new URLSearchParams();
    params.append("provider", provider.trim());
    params.append("category", category.trim());
    params.append("tenant_id", tenantId.toString().trim());

    const queryString = params.toString();
    return this.request(
      `/inventory-import/jobs/${importId}/metadata?${queryString}`,
    );
  }

  // Merge import job to main inventory
  async mergeImportJob(importId) {
    return this.request(`/inventory-import/jobs/${importId}/merge`, {
      method: "POST",
    });
  }

  async getImportFormatInfo(provider, category) {
    const params = new URLSearchParams();
    params.append("provider", provider);
    params.append("category", category);
    return this.request(`/inventory-import/format-info?${params.toString()}`);
  }

  async validateReplaceFile(formData, retryCount = 0) {
    return this._postMultipart("/inventory-import/validate-file", formData, retryCount);
  }

  async previewReplaceInventory(formData, retryCount = 0) {
    return this._postMultipart("/inventory-import/preview-replace", formData, retryCount);
  }

  async executeReplaceInventory(formData, retryCount = 0) {
    return this._postMultipart("/inventory-import/replace", formData, retryCount);
  }

  async executeReplaceFromJob(payload) {
    return this.request(`/inventory-import/replace-from-job`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async getReplaceHistory(page = 1, pageSize = 20) {
    const params = new URLSearchParams();
    params.append("page", String(page));
    params.append("pageSize", String(pageSize));
    return this.request(`/inventory-import/replace-history?${params.toString()}`);
  }

  async rollbackReplaceHistory(historyId) {
    return this.request(`/inventory-import/replace-history/${historyId}/rollback`, {
      method: "POST",
    });
  }

  async downloadReplaceHistoryFile(historyId, fallbackFileName) {
    if (checkAuthenticationFailed()) {
      const error = new Error("Authentication failed - redirecting to login");
      error.status = 401;
      error.isAuthError = true;
      throw error;
    }
    const url = `${API_BASE_URL}/inventory-import/replace-history/${historyId}/download`;
    const token = getAccessToken();
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(url, { method: "GET", headers });
    if (!response.ok) {
      let message = "Failed to download import file";
      try {
        const err = await response.json();
        message = err.message || err.error || message;
      } catch (_) {}
      throw new Error(message);
    }
    const blob = await response.blob();
    const contentDisposition = response.headers.get("Content-Disposition");
    let filename = fallbackFileName || `import_${historyId}`;
    if (contentDisposition) {
      const m = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (m && m[1]) filename = m[1].replace(/['"]/g, "");
    }
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    }, 100);
    return { success: true, filename };
  }

  async downloadImportTemplate(provider, category, tenantId) {
    const params = new URLSearchParams();
    params.append("provider", provider);
    params.append("category", category);
    if (tenantId) params.append("tenant_id", tenantId);
    const url = `${API_BASE_URL}/inventory-import/template?${params.toString()}`;
    const token = getAccessToken();
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(url, { method: "GET", headers });
    if (!response.ok) {
      let message = "Failed to download template";
      try {
        const err = await response.json();
        message = err.message || err.error || message;
      } catch (_) {}
      throw new Error(message);
    }
    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = `${provider}_${category}_template.xlsx`.replace(/\s+/g, "_");
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(objectUrl);
    return { success: true };
  }

  async _postMultipart(endpoint, formData, retryCount = 0) {
    if (checkAuthenticationFailed()) {
      const error = new Error("Authentication failed - redirecting to login");
      error.status = 401;
      error.isAuthError = true;
      throw error;
    }

    const url = `${API_BASE_URL}${endpoint}`;
    const token = getAccessToken();
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers,
        body: formData,
      });

      if (response.status === 401 && retryCount === 0) {
        const refreshResult = await refreshAccessToken();
        if (refreshResult.success) {
          return this._postMultipart(endpoint, formData, retryCount + 1);
        }
        markAuthenticationFailed();
        const error = new Error("Session expired. Please log in again.");
        error.status = 401;
        error.isAuthError = true;
        throw error;
      }

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const error = new Error(
          data.message || data.error || "Request failed",
        );
        error.status = response.status;
        error.details = data.details || data.data || null;
        error.payload = data;
        throw error;
      }
      return data;
    } catch (error) {
      if (error.status || error.isAuthError) throw error;
      throw error;
    }
  }

  // Standardize all addresses for an import job
  async standardizeAllImportAddresses(importId) {
    return this.request(
      `/address-standardization/import-jobs/${importId}/standardize-all`,
      {
        method: "POST",
      },
    );
  }

  // Get inventory data with filters and pagination
  async getInventoryData(filters = {}) {
    const {
      page = 1,
      pageSize = 20,
      provider,
      category,
      status,
      serviceType,
      tenant_id,
      search,
      sortBy,
      sortOrder,
    } = filters;

    // Build query parameters
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("pageSize", pageSize);

    // Always include tenant_id
    if (tenant_id) {
      params.append("tenant_id", tenant_id);
    }

    // Add optional filters - handle arrays
    if (provider) {
      if (Array.isArray(provider) && provider.length > 0) {
        provider.forEach((p) => params.append("provider", p));
      } else if (!Array.isArray(provider)) {
        params.append("provider", provider);
      }
    }
    if (category) {
      if (Array.isArray(category) && category.length > 0) {
        category.forEach((c) => params.append("category", c));
      } else if (!Array.isArray(category)) {
        params.append("category", category);
      }
    }
    if (status) {
      if (Array.isArray(status) && status.length > 0) {
        status.forEach((s) => params.append("status", s));
      } else if (!Array.isArray(status)) {
        params.append("status", status);
      }
    }
    if (serviceType) {
      if (Array.isArray(serviceType) && serviceType.length > 0) {
        serviceType.forEach((st) => params.append("service_type", st));
      } else if (!Array.isArray(serviceType)) {
        params.append("service_type", serviceType);
      }
    }
    if (search) params.append("search", search);

    // Add sorting parameters if provided
    if (sortBy) {
      params.append("sortBy", sortBy);
      if (sortOrder) {
        params.append("sortOrder", sortOrder);
      }
    }

    // Use the correct API endpoint
    return this.request(`/inventory-import/inventory?${params.toString()}`);
  }

  // Get grouped inventory data with filters and pagination
  async getGroupedInventoryData(filters = {}) {
    const {
      page = 1,
      pageSize = 20,
      provider,
      category,
      status,
      serviceType,
      billingAccount,
      tenant_id,
      search,
      sortBy,
      sortOrder,
      siteRegion,
      siteClassification,
      siteGeneralManagerId,
      siteSdWanAppliance,
      siteStatus,
      circuitAction,
      contractEndsWithin,
    } = filters;

    // Build query parameters
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("pageSize", pageSize);

    // Always include tenant_id
    if (tenant_id) {
      params.append("tenant_id", tenant_id);
    }

    // Add optional filters - handle arrays
    if (provider) {
      if (Array.isArray(provider) && provider.length > 0) {
        provider.forEach((p) => params.append("provider", p));
      } else if (!Array.isArray(provider)) {
        params.append("provider", provider);
      }
    }
    if (category) {
      if (Array.isArray(category) && category.length > 0) {
        category.forEach((c) => params.append("category", c));
      } else if (!Array.isArray(category)) {
        params.append("category", category);
      }
    }
    if (status) {
      if (Array.isArray(status) && status.length > 0) {
        status.forEach((s) => params.append("status", s));
      } else if (!Array.isArray(status)) {
        params.append("status", status);
      }
    }
    if (serviceType) {
      if (Array.isArray(serviceType) && serviceType.length > 0) {
        serviceType.forEach((st) => params.append("service_type", st));
      } else if (!Array.isArray(serviceType)) {
        params.append("service_type", serviceType);
      }
    }
    if (billingAccount) {
      if (Array.isArray(billingAccount) && billingAccount.length > 0) {
        billingAccount.forEach((ba) => params.append("billing_account", ba));
      } else if (!Array.isArray(billingAccount)) {
        params.append("billing_account", billingAccount);
      }
    }
    if (search) params.append("search", search);

    const appendMultiParam = (queryKey, val) => {
      if (!val) return;
      const arr = Array.isArray(val) ? val : [val];
      arr.forEach((v) => {
        if (v != null && String(v).trim() !== "") params.append(queryKey, v);
      });
    };
    appendMultiParam("site_region", siteRegion);
    appendMultiParam("site_classification", siteClassification);
    appendMultiParam("general_manager_id", siteGeneralManagerId);
    appendMultiParam("sd_wan_appliance_name", siteSdWanAppliance);
    appendMultiParam("site_status", siteStatus);
    appendMultiParam("circuit_action", circuitAction);
    if (
      contractEndsWithin != null &&
      String(contractEndsWithin).trim() !== "" &&
      Number(contractEndsWithin) > 0
    ) {
      params.append("contract_ends_within", String(contractEndsWithin));
    }

    // Add sorting parameters if provided
    if (sortBy) {
      params.append("sortBy", sortBy);
      if (sortOrder) {
        params.append("sortOrder", sortOrder);
      }
    }

    // Use the grouped API endpoint
    return this.request(
      `/inventory-import/inventory/grouped?${params.toString()}`,
    );
  }

  async getSiteInventoryFilterOptions(tenantId) {
    const params = new URLSearchParams();
    params.append("tenant_id", tenantId);
    return this.request(`/sites/inventory-filter-options?${params.toString()}`);
  }

  // Get dropdown values (providers, categories, statuses) from Inventory table
  async getDropdownValues() {
    return this.request("/inventory-import/dropdown-values");
  }

  /** Register a billing account for a provider (persists for future dropdowns). */
  async registerBillingAccountOption(provider, billingAccount, glCode) {
    return this.request("/inventory-import/billing-account-options", {
      method: "POST",
      body: JSON.stringify({
        provider: provider || "",
        billing_account: billingAccount || "",
        ...(glCode != null && String(glCode).trim() !== ""
          ? { gl_code: String(glCode).trim() }
          : {}),
      }),
    });
  }

  /** Register a provider name (persists for future dropdowns). */
  async registerProviderOption(provider) {
    return this.request("/inventory-import/provider-options", {
      method: "POST",
      body: JSON.stringify({ provider: provider || "" }),
    });
  }

  // Get single inventory record
  async getInventoryRecord(recordId, tenantId) {
    const params = new URLSearchParams();
    if (tenantId) {
      params.append("tenant_id", tenantId);
    }
    return this.request(
      `/inventory-import/inventory/${recordId}?${params.toString()}`,
    );
  }

  // Update inventory record
  async updateInventoryRecord(recordId, data, tenantId) {
    const params = new URLSearchParams();
    if (tenantId) {
      params.append("tenant_id", tenantId);
    }
    return this.request(
      `/inventory-import/inventory/${recordId}?${params.toString()}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
  }

  // Create new inventory record
  async createInventoryRecord(data) {
    return this.request(`/inventory-import/inventory`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // Track import changes
  async trackImportChanges(importId, changeData) {
    return this.request(`/inventory-import/jobs/${importId}/changes`, {
      method: "POST",
      body: JSON.stringify(changeData),
    });
  }

  // Update import record (for editing records in import preview)
  async updateImportRecord(importId, recordId, data) {
    return this.request(
      `/inventory-import/jobs/${importId}/records/${recordId}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
  }

  // Get single import record
  async getImportRecord(importId, recordId) {
    return this.request(
      `/inventory-import/jobs/${importId}/records/${recordId}`,
    );
  }

  // Get column mapping from database
  async getColumnMapping(provider, category, tenantId, format = "flat") {
    const params = new URLSearchParams();
    if (provider) params.append("provider", provider);
    if (category) params.append("category", category);
    if (tenantId) params.append("tenant_id", tenantId);
    if (format) params.append("format", format);

    const queryString = params.toString();
    const url = queryString
      ? `/inventory-import/mapping?${queryString}`
      : "/inventory-import/mapping";

    return this.request(url);
  }

  // Get all mappings
  async getAllMappings() {
    return this.request("/inventory-import/mapping");
  }

  // Get mappings by tenant
  async getMappingsByTenant() {
    return this.request("/inventory-import/mapping?format=by_tenant");
  }

  // Truncate inventory tables
  async truncateTables() {
    return this.request("/inventory/truncate-tables", {
      method: "POST",
    });
  }

  // Get count of records matching filters (delete preview)
  async getDeletePreviewCount(filters = {}) {
    const {
      tenant_id,
      provider,
      category,
      status,
      service_type,
      billing_account,
    } = filters;

    const params = new URLSearchParams();
    if (tenant_id) params.append("tenant_id", tenant_id);

    // Handle arrays properly - only append if non-empty
    if (provider) {
      if (Array.isArray(provider) && provider.length > 0) {
        provider.forEach((p) => params.append("provider", p));
      } else if (!Array.isArray(provider)) {
        params.append("provider", provider);
      }
    }
    if (category) {
      if (Array.isArray(category) && category.length > 0) {
        category.forEach((c) => params.append("category", c));
      } else if (!Array.isArray(category)) {
        params.append("category", category);
      }
    }
    if (status) {
      if (Array.isArray(status) && status.length > 0) {
        status.forEach((s) => params.append("status", s));
      } else if (!Array.isArray(status)) {
        params.append("status", status);
      }
    }
    if (service_type) {
      if (Array.isArray(service_type) && service_type.length > 0) {
        service_type.forEach((st) => params.append("service_type", st));
      } else if (!Array.isArray(service_type)) {
        params.append("service_type", service_type);
      }
    }
    // Handle billing_account filter (can be array or single value)
    if (billing_account) {
      if (Array.isArray(billing_account) && billing_account.length > 0) {
        billing_account.forEach((ba) => params.append("billing_account", ba));
      } else if (!Array.isArray(billing_account)) {
        params.append("billing_account", billing_account);
      }
    }

    return this.request(`/inventory/delete-preview?${params.toString()}`);
  }

  // Delete inventory records matching filters
  async deleteFilteredInventory(filters = {}) {
    const {
      tenant_id,
      provider,
      category,
      status,
      service_type,
      billing_account,
      skip_delete_snapshot,
      preserve_existing_delete_snapshot,
    } = filters;

    // Build request body with filter parameters
    const body = {
      tenant_id,
    };

    // Only include filters if they have values
    if (
      provider &&
      ((Array.isArray(provider) && provider.length > 0) ||
        !Array.isArray(provider))
    ) {
      body.provider = provider;
    }
    if (
      category &&
      ((Array.isArray(category) && category.length > 0) ||
        !Array.isArray(category))
    ) {
      body.category = category;
    }
    if (
      status &&
      ((Array.isArray(status) && status.length > 0) || !Array.isArray(status))
    ) {
      body.status = status;
    }
    if (
      service_type &&
      ((Array.isArray(service_type) && service_type.length > 0) ||
        !Array.isArray(service_type))
    ) {
      body.service_type = service_type;
    }
    // Handle billing_account filter (can be array or single value)
    if (
      billing_account &&
      ((Array.isArray(billing_account) && billing_account.length > 0) ||
        !Array.isArray(billing_account))
    ) {
      body.billing_account = billing_account;
    }

    if (skip_delete_snapshot) {
      body.skip_delete_snapshot = true;
    }
    if (preserve_existing_delete_snapshot) {
      body.preserve_existing_delete_snapshot = true;
    }

    return this.request("/inventory/delete-filtered", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  // Delete a single inventory item by ID (optimistic lock via version query param)
  async deleteInventoryItem(recordId, snapshotOptions = {}) {
    const params = new URLSearchParams();
    if (snapshotOptions.version != null) {
      params.set("version", String(snapshotOptions.version));
    }
    if (snapshotOptions.skip_delete_snapshot) {
      params.set("skip_delete_snapshot", "true");
    }
    if (snapshotOptions.preserve_existing_delete_snapshot) {
      params.set("preserve_existing_delete_snapshot", "true");
    }
    const qs = params.toString();
    const path = qs
      ? `/inventory/${encodeURIComponent(recordId)}?${qs}`
      : `/inventory/${encodeURIComponent(recordId)}`;
    return this.request(path, {
      method: "DELETE",
    });
  }

  // Standardize address without saving
  async standardizeAddress(addressData) {
    return this.request("/address-standardization/standardize", {
      method: "POST",
      body: JSON.stringify(addressData),
    });
  }

  // Standardize and save address to database
  async standardizeAndSaveAddress(addressData) {
    return this.request("/address-standardization/standardize-and-save", {
      method: "POST",
      body: JSON.stringify(addressData),
    });
  }

  // Standardize and save for single record only: creates new address, moves only this circuit to it
  async standardizeAndSaveAddressForRecord(payload) {
    return this.request(
      "/address-standardization/standardize-and-save-for-record",
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );
  }

  // Get all addresses with pagination and sorting
  async getAddresses(
    page = 1,
    pageSize = 50,
    sortBy = null,
    sortOrder = null,
    searchQuery = null,
  ) {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("pageSize", pageSize);
    if (sortBy) {
      params.append("sortBy", sortBy);
      params.append("sortOrder", sortOrder || "asc");
    }
    if (searchQuery && searchQuery.trim().length >= 3) {
      params.append("search", searchQuery.trim());
    }
    return this.request(`/addresses?${params.toString()}`);
  }

  // Get grouped addresses with pagination and sorting
  async getGroupedAddresses(
    page = 1,
    pageSize = 50,
    sortBy = null,
    sortOrder = null,
    searchQuery = null,
    tenantId = null,
  ) {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("pageSize", pageSize);

    // Add tenant_id if provided
    if (tenantId) {
      params.append("tenant_id", tenantId);
    }

    if (sortBy) {
      params.append("sortBy", sortBy);
      params.append("sortOrder", sortOrder || "asc");
    }
    if (searchQuery && searchQuery.trim().length >= 3) {
      params.append("search", searchQuery.trim());
    }
    return this.request(`/addresses/grouped?${params.toString()}`);
  }

  async getMasterAddressStats(tenantId) {
    const params = new URLSearchParams();
    if (tenantId) params.append("tenant_id", tenantId);
    return this.request(`/addresses/master-stats?${params.toString()}`);
  }

  async getMasterAddresses({
    page = 1,
    pageSize = 50,
    sortBy = "std_street_address",
    sortOrder = "asc",
    search = "",
    city = "",
    state = "",
    zip = "",
    show = "all",
    tenantId = null,
  } = {}) {
    const params = new URLSearchParams();
    params.append("page", String(page));
    params.append("pageSize", String(pageSize));
    if (tenantId) params.append("tenant_id", tenantId);
    if (sortBy) {
      params.append("sortBy", sortBy);
      params.append("sortOrder", sortOrder || "asc");
    }
    if (search) params.append("search", search);
    if (city) params.append("city", city);
    if (state) params.append("state", state);
    if (zip) params.append("zip", zip);
    if (show) params.append("show", show);
    return this.request(`/addresses/masters?${params.toString()}`);
  }

  async getNeedsMasterAddresses({
    page = 1,
    pageSize = 100,
    search = "",
    state = "",
    zip = "",
    tenantId = null,
  } = {}) {
    const params = new URLSearchParams();
    params.append("page", String(page));
    params.append("pageSize", String(pageSize));
    if (tenantId) params.append("tenant_id", tenantId);
    if (search) params.append("search", search);
    if (state) params.append("state", state);
    if (zip) params.append("zip", zip);
    return this.request(`/addresses/needs-master?${params.toString()}`);
  }

  async getMasterAddressOptions(tenantId, search = "") {
    const params = new URLSearchParams();
    if (tenantId) params.append("tenant_id", tenantId);
    if (search) params.append("search", search);
    return this.request(`/addresses/masters/options?${params.toString()}`);
  }

  async linkAddressesToMaster({ tenantId, addressIds, master, howLinked }) {
    return this.request("/addresses/link-to-master", {
      method: "POST",
      body: JSON.stringify({
        tenant_id: tenantId,
        address_ids: addressIds,
        master,
        how_linked: howLinked,
      }),
    });
  }

  async createMasterAddress(data) {
    return this.request("/addresses/masters", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateMasterAddress(data) {
    return this.request("/addresses/masters", {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteMasterAddress({ tenantId, master }) {
    return this.request("/addresses/masters", {
      method: "DELETE",
      body: JSON.stringify({
        tenant_id: tenantId,
        master,
      }),
    });
  }

  async normalizeAddressPreview(data) {
    return this.request("/addresses/normalize-preview", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // Get total count of all addresses in the database
  async getTotalAddressCount(tenantId = null) {
    console.log("🔗 API Base URL:", API_BASE_URL);
    const params = new URLSearchParams();
    if (tenantId) {
      params.append("tenant_id", tenantId);
    }
    const url = `/addresses/total-count${
      params.toString() ? "?" + params.toString() : ""
    }`;
    console.log("🔗 Full URL:", `${API_BASE_URL}${url}`);
    return this.request(url);
  }

  // Get single address by ID
  async getAddressById(addressId) {
    return this.request(`/addresses/${addressId}`);
  }

  // Update input address fields (regular fields only)
  async updateInputAddress(addressId, data) {
    return this.request(`/addresses/${addressId}/input`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  // Update standardized address fields
  async updateAddress(addressId, data) {
    return this.request(`/addresses/${addressId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  // Create a new address
  async createAddress(data) {
    return this.request(`/addresses`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // Get preview of what will be affected by deleting all addresses
  async getDeleteAllAddressesPreview(tenantId) {
    const params = new URLSearchParams();
    if (tenantId) params.append("tenant_id", tenantId);

    return this.request(`/addresses/delete-all-preview?${params.toString()}`);
  }

  // Delete all addresses
  async deleteAllAddresses(tenantId) {
    return this.request("/addresses/delete-all", {
      method: "DELETE",
      body: JSON.stringify({ tenant_id: tenantId }),
    });
  }

  async getDedupeAddressesPreview(tenantId) {
    const params = new URLSearchParams();
    if (tenantId) params.append("tenant_id", tenantId);
    return this.request(`/addresses/dedupe-preview?${params.toString()}`);
  }

  async dedupeAddresses(tenantId) {
    return this.request("/addresses/dedupe", {
      method: "POST",
      body: JSON.stringify({ tenant_id: tenantId }),
    });
  }

  // --- Site attributes (region, GM, contacts, etc.) ---
  async getSiteByAddressKey(tenantId, addressKey, options = {}) {
    const params = new URLSearchParams();
    params.append("tenant_id", tenantId);
    if (addressKey.std_street_address != null) params.append("std_street_address", addressKey.std_street_address);
    if (addressKey.std_city != null) params.append("std_city", addressKey.std_city);
    if (addressKey.std_state != null) params.append("std_state", addressKey.std_state);
    if (addressKey.std_zip != null) params.append("std_zip", addressKey.std_zip);
    if (addressKey.std_country != null) params.append("std_country", addressKey.std_country);
    if (options.create === false) params.append("create", "false");
    return this.request(`/sites/by-address?${params.toString()}`);
  }

  async createSite(data) {
    return this.request("/sites", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateSite(siteId, data) {
    return this.request(`/sites/${siteId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async getSiteAddresses(siteId, tenantId) {
    const params = new URLSearchParams();
    if (tenantId != null) params.append("tenant_id", tenantId);
    const q = params.toString();
    return this.request(`/sites/${siteId}/addresses${q ? `?${q}` : ""}`);
  }

  async getRegions() {
    return this.request("/sites/regions");
  }

  async getRegionByZip(zip) {
    const params = new URLSearchParams();
    params.append("zip", zip);
    return this.request(`/sites/region-by-zip?${params.toString()}`);
  }

  async getSiteClassifications(tenantId) {
    const params = new URLSearchParams();
    if (tenantId) params.append("tenant_id", tenantId);
    const q = params.toString();
    return this.request(`/sites/classifications${q ? `?${q}` : ""}`);
  }

  async getGeneralManagers(tenantId) {
    const params = new URLSearchParams();
    params.append("tenant_id", tenantId);
    return this.request(`/sites/general-managers?${params.toString()}`);
  }

  async createGeneralManager(tenantId, name) {
    return this.request("/sites/general-managers", {
      method: "POST",
      body: JSON.stringify({ tenant_id: tenantId, name }),
    });
  }

  // Export inventory records to Excel with secure HTTPS handling
  async exportInventoryRecords(filters = {}) {
    const { tenant_id, provider, category, status, search, sortBy, sortOrder } =
      filters;

    const params = new URLSearchParams();
    if (tenant_id) params.append("tenant_id", tenant_id);

    // Handle arrays properly - only append if non-empty
    // This ensures export uses the same filters as displayed data
    if (provider) {
      if (Array.isArray(provider) && provider.length > 0) {
        provider.forEach((p) => params.append("provider", p));
      } else if (!Array.isArray(provider)) {
        params.append("provider", provider);
      }
    }
    if (category) {
      if (Array.isArray(category) && category.length > 0) {
        category.forEach((c) => params.append("category", c));
      } else if (!Array.isArray(category)) {
        params.append("category", category);
      }
    }
    if (status) {
      if (Array.isArray(status) && status.length > 0) {
        status.forEach((s) => params.append("status", s));
      } else if (!Array.isArray(status)) {
        params.append("status", status);
      }
    }
    if (search) params.append("search", search);
    if (sortBy) params.append("sortBy", sortBy);
    if (sortOrder) params.append("sortOrder", sortOrder);

    const url = `${API_BASE_URL}/inventory-import/inventory/export?${params.toString()}`;

    // Validate URL protocol in production to prevent security warnings
    if (process.env.NODE_ENV === "production" && url.startsWith("http://")) {
      console.error(
        "SECURITY WARNING: Attempting to download file over HTTP in production. " +
          "This will cause browser security warnings. Please configure REACT_APP_API_BASE_URL with HTTPS.",
      );
    }

    try {
      const token = getAccessToken();
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers,
        credentials: "same-origin", // Include credentials for HTTPS requests
      });

      if (!response.ok) {
        let errorMessage = "Failed to export records";
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          // If JSON parsing fails, use default message
        }
        const error = new Error(errorMessage);
        error.status = response.status;
        throw error;
      }

      // Validate response is actually a file (not JSON error)
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        // Backend sent JSON instead of file - this is an error
        const errorData = await response.json();
        throw new Error(
          errorData.message || errorData.error || "Export failed",
        );
      }

      // Get filename from Content-Disposition header or use default with timestamp
      const contentDisposition = response.headers.get("Content-Disposition");
      const timestamp = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
      let filename = `inventory_records_${timestamp}.xlsx`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
        );
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, "");
          try {
            filename = decodeURIComponent(filename);
          } catch (e) {
            // If decoding fails, use as-is
          }
        }
      }

      // Convert response to blob
      const blob = await response.blob();

      // Validate blob is not empty
      if (blob.size === 0) {
        throw new Error("Downloaded file is empty. No records to export.");
      }

      // Create blob with explicit Excel MIME type to ensure proper handling
      const excelBlob = new Blob([blob], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Create download URL and trigger download
      const downloadUrl = window.URL.createObjectURL(excelBlob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      link.style.display = "none"; // Hide link element

      // Append to body, click, and cleanup
      document.body.appendChild(link);
      link.click();

      // Cleanup with small delay to ensure download starts
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      }, 100);

      return { success: true, filename };
    } catch (error) {
      // Handle network errors
      if (
        error.message === "Failed to fetch" ||
        error.message.includes("Failed to fetch") ||
        error.name === "TypeError" ||
        (error.message && error.message.toLowerCase().includes("network"))
      ) {
        const networkError = new Error("Unable to load the data");
        networkError.status = 0;
        networkError.isNetworkError = true;
        networkError.originalError = error;
        throw networkError;
      }
      console.error("Export API request failed:", error);
      throw error;
    }
  }

  // Download Custom category import template
  async downloadCustomTemplate(tenantId) {
    const params = new URLSearchParams();
    if (tenantId) params.append("tenant_id", tenantId);

    const url = `${API_BASE_URL}/inventory-import/template/custom?${params.toString()}`;

    try {
      const token = getAccessToken();
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        // Check if response is JSON (error response)
        const contentType = response.headers.get("content-type");
        let errorMessage = "Failed to download template";

        if (contentType && contentType.includes("application/json")) {
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;
          } catch {
            // If JSON parsing fails, use default message
          }
        } else {
          // For non-JSON errors, try to get text (might be HTML error page)
          try {
            const errorText = await response.text();
            if (errorText && errorText.length < 500) {
              // Check if it's a JSON string
              try {
                const jsonError = JSON.parse(errorText);
                errorMessage =
                  jsonError.message || jsonError.error || errorMessage;
              } catch {
                // Not JSON, might be HTML or plain text
                if (
                  errorText.includes("error") ||
                  errorText.includes("Error")
                ) {
                  errorMessage =
                    "Server error while downloading template. Please try again.";
                }
              }
            }
          } catch {
            // Keep default message if all parsing fails
          }
        }

        const error = new Error(errorMessage);
        error.status = response.status;
        throw error;
      }

      // Validate response is actually an Excel file
      const contentType = response.headers.get("content-type");
      const expectedTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "application/octet-stream",
      ];

      if (
        contentType &&
        !expectedTypes.some((type) => contentType.includes(type))
      ) {
        console.warn(
          `Unexpected content type: ${contentType}. Proceeding with download anyway.`,
        );
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = "custom_import_template.xlsx";
      if (contentDisposition) {
        // Handle both quoted and unquoted filenames, and encoded filenames
        const filenameMatch = contentDisposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
        );
        if (filenameMatch && filenameMatch[1]) {
          // Remove quotes if present
          filename = filenameMatch[1].replace(/['"]/g, "");
          // Decode URI if encoded (handles filename*=UTF-8''encoded%20name.xlsx)
          try {
            filename = decodeURIComponent(filename);
          } catch (e) {
            // If decoding fails, use as-is
          }
        }
      }

      // Convert response to blob
      const blob = await response.blob();

      // Validate blob is not empty
      if (blob.size === 0) {
        throw new Error("Downloaded template file is empty. Please try again.");
      }

      // Validate blob type (should be Excel) - check if it might be an error message
      if (
        !blob.type ||
        !expectedTypes.some((type) => blob.type.includes(type))
      ) {
        // Check if blob might be an error message (small size suggests text/JSON)
        if (blob.size < 1000) {
          const text = await blob.text();
          try {
            const jsonError = JSON.parse(text);
            throw new Error(
              jsonError.message ||
                jsonError.error ||
                "Failed to download template",
            );
          } catch (e) {
            // If not JSON, it might be HTML error page
            if (text.includes("error") || text.includes("Error")) {
              throw new Error(
                "Server error while downloading template. Please try again.",
              );
            }
            // If it's not an obvious error, proceed with download (might be valid Excel)
          }
        }
      }

      // Download the blob
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();

      // Cleanup with small delay to ensure download starts
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      }, 100);

      return { success: true, filename };
    } catch (error) {
      // Handle network errors
      if (
        error.message === "Failed to fetch" ||
        error.message.includes("Failed to fetch") ||
        error.name === "TypeError" ||
        (error.message && error.message.toLowerCase().includes("network"))
      ) {
        const networkError = new Error("Unable to load the data");
        networkError.status = 0;
        networkError.isNetworkError = true;
        networkError.originalError = error;
        throw networkError;
      }
      console.error("Template download API request failed:", error);
      throw error;
    }
  }

  // --- Monthly Invoices API ---
  async uploadMonthlyInvoice(formData, retryCount = 0) {
    if (checkAuthenticationFailed()) {
      const error = new Error("Authentication failed - redirecting to login");
      error.status = 401;
      error.isAuthError = true;
      throw error;
    }
    const url = `${API_BASE_URL}/invoices/upload`;
    const token = getAccessToken();
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(url, { method: "POST", headers, body: formData });
    if (response.status === 401 && retryCount === 0) {
      const refreshResult = await refreshAccessToken();
      if (refreshResult.success) return this.uploadMonthlyInvoice(formData, 1);
      markAuthenticationFailed();
      const err = new Error("Session expired. Please log in again.");
      err.status = 401;
      err.isAuthError = true;
      throw err;
    }
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(data.message || data.error || "Upload failed");
      err.status = response.status;
      err.details = data.details;
      throw err;
    }
    return data;
  }

  async createInvoiceUploadSession() {
    return this.request("/invoices/upload-sessions", { method: "POST" });
  }

  async uploadInvoiceSessionFiles(sessionId, files, retryCount = 0) {
    if (checkAuthenticationFailed()) {
      const error = new Error("Authentication failed - redirecting to login");
      error.status = 401;
      error.isAuthError = true;
      throw error;
    }
    const url = `${API_BASE_URL}/invoices/upload-sessions/${sessionId}/files`;
    const token = getAccessToken();
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const formData = new FormData();
    for (const file of files) {
      formData.append("files", file);
    }
    const response = await fetch(url, { method: "POST", headers, body: formData });
    if (response.status === 401 && retryCount === 0) {
      const refreshResult = await refreshAccessToken();
      if (refreshResult.success) {
        return this.uploadInvoiceSessionFiles(sessionId, files, 1);
      }
      markAuthenticationFailed();
      const err = new Error("Session expired. Please log in again.");
      err.status = 401;
      err.isAuthError = true;
      throw err;
    }
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(data.message || data.error || "Upload failed");
      err.status = response.status;
      throw err;
    }
    return data;
  }

  async processInvoiceUploadSession(sessionId) {
    return this.request(`/invoices/upload-sessions/${sessionId}/process`, {
      method: "POST",
    });
  }

  async getInvoiceUploadSession(sessionId) {
    return this.request(`/invoices/upload-sessions/${sessionId}`);
  }

  async confirmInvoiceUploadSession(sessionId, body) {
    return this.request(`/invoices/upload-sessions/${sessionId}/confirm`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  async cancelInvoiceUploadSession(sessionId) {
    return this.request(`/invoices/upload-sessions/${sessionId}/cancel`, {
      method: "POST",
    });
  }

  async discardInvoiceUploadSessionFile(sessionId, fileId) {
    return this.request(
      `/invoices/upload-sessions/${sessionId}/files/${fileId}`,
      { method: "DELETE" },
    );
  }

  async getInvoiceUploadSessionFilePdf(sessionId, fileId, retryCount = 0) {
    if (checkAuthenticationFailed()) {
      const error = new Error("Authentication failed - redirecting to login");
      error.status = 401;
      error.isAuthError = true;
      throw error;
    }
    const url = `${API_BASE_URL}/invoices/upload-sessions/${sessionId}/files/${fileId}/pdf`;
    const token = getAccessToken();
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(url, { method: "GET", headers });
    if (response.status === 401 && retryCount === 0) {
      const refreshResult = await refreshAccessToken();
      if (refreshResult.success) {
        return this.getInvoiceUploadSessionFilePdf(sessionId, fileId, 1);
      }
      markAuthenticationFailed();
      const err = new Error("Session expired. Please log in again.");
      err.status = 401;
      err.isAuthError = true;
      throw err;
    }
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || data.error || "Failed to load PDF");
    }
    const blob = await response.blob();
    if (!blob || blob.size === 0) {
      throw new Error("Invoice PDF was empty");
    }
    return blob.type === "application/pdf"
      ? blob
      : new Blob([blob], { type: "application/pdf" });
  }

  async getInvoiceProviders() {
    return this.request("/invoices/providers");
  }

  async getInvoiceBillingAccounts(provider) {
    const params = new URLSearchParams({ provider: provider || "" });
    return this.request(`/invoices/billing-accounts?${params.toString()}`);
  }

  async registerInvoiceBillingAccount(provider, billingAccount, expectedMrc, glCode) {
    return this.request("/invoices/billing-accounts/register", {
      method: "POST",
      body: JSON.stringify({
        provider: provider || "",
        billing_account: billingAccount || "",
        ...(expectedMrc != null && String(expectedMrc).trim() !== ""
          ? { expected_mrc: expectedMrc }
          : {}),
        ...(glCode != null && String(glCode).trim() !== ""
          ? { gl_code: String(glCode).trim() }
          : {}),
      }),
    });
  }

  async closeInvoiceBillingAccount(provider, billingAccount) {
    return this.request("/invoices/billing-accounts/close", {
      method: "POST",
      body: JSON.stringify({
        provider: provider || "",
        billing_account: billingAccount || "",
      }),
    });
  }

  /**
   * @param {{ invoiceMonth?: string, provider?: string, billingAccount?: string } | string} [opts]
   * @param {string} [billingAccount] - Legacy: listInvoices(provider, billingAccount)
   */
  async listInvoices(opts, billingAccount) {
    const params = new URLSearchParams();
    if (opts != null && typeof opts === "object") {
      if (opts.invoiceMonth) params.set("invoice_month", opts.invoiceMonth);
      if (opts.provider) params.set("provider", opts.provider);
      if (opts.billingAccount) params.set("billing_account", opts.billingAccount);
    } else {
      const provider = typeof opts === "string" ? opts : "";
      params.set("provider", provider);
      params.set("billing_account", billingAccount || "");
    }
    return this.request(`/invoices?${params.toString()}`);
  }

  async deleteInvoice(id, version) {
    const params = new URLSearchParams();
    if (version != null) params.set("version", version);
    const qs = params.toString();
    return this.request(`/invoices/${id}${qs ? `?${qs}` : ""}`, { method: "DELETE" });
  }

  async updateInvoiceAmounts(id, payload) {
    return this.request(`/invoices/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  }

  async generateInvoiceNote(id) {
    return this.request(`/invoices/${id}/generate-note`, {
      method: "POST",
    });
  }

  async markInvoiceReviewed(id) {
    return this.request(`/invoices/${id}/mark-reviewed`, {
      method: "POST",
    });
  }

  async getInvoiceNoteAutomationSettings() {
    return this.request("/invoices/note-automation-settings", { method: "GET" });
  }

  async sendInvoiceEmails(invoiceIds) {
    return this.request("/invoices/send-email", {
      method: "POST",
      body: JSON.stringify({ invoiceIds }),
    });
  }

  async sendInvoiceEmail(id) {
    return this.request(`/invoices/${id}/send-email`, {
      method: "POST",
    });
  }

  async downloadInvoice(id) {
    if (checkAuthenticationFailed()) {
      const error = new Error("Authentication failed - redirecting to login");
      error.status = 401;
      error.isAuthError = true;
      throw error;
    }
    const url = `${API_BASE_URL}/invoices/${id}/download`;
    const token = getAccessToken();
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(url, { method: "GET", headers });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || data.error || "Download failed");
    }
    const blob = await response.blob();
    const contentDisposition = response.headers.get("Content-Disposition");
    let filename = `invoice_${id}.pdf`;
    if (contentDisposition) {
      const m = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (m && m[1]) filename = m[1].replace(/['"]/g, "");
    }
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    }, 100);
    return { success: true, filename };
  }

  async getCreditLedger() {
    return this.request("/invoices/credit-ledger");
  }

  async resolveCredit({ provider, billingAccount, resolveNote, expectedVersion }) {
    return this.request("/invoices/credit-ledger/resolve", {
      method: "POST",
      body: JSON.stringify({
        provider,
        billing_account: billingAccount,
        resolve_note: resolveNote,
        ...(expectedVersion != null ? { expected_version: expectedVersion } : {}),
      }),
    });
  }

  async reopenCredit({ provider, billingAccount, version }) {
    return this.request("/invoices/credit-ledger/reopen", {
      method: "POST",
      body: JSON.stringify({
        provider,
        billing_account: billingAccount,
        version,
      }),
    });
  }

  /** Header global search: circuits, addresses, billing accounts, invoices. */
  async globalSearch(query) {
    const params = new URLSearchParams();
    params.append("q", query == null ? "" : String(query));
    return this.request(`/search?${params.toString()}`);
  }
}

export const apiService = new ApiService();
